import { useRef, useState, useEffect } from "react";
import "./pieChartComponent.scss";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  PieLabelRenderProps,
} from "recharts";

interface DataEntry {
  data: {
    name: string;
    value: number;
  }[];
}

const COLORS = ["#B3D588", "#E19083", "#D8DADF", "#F4C482", "#3D424A"];

const RADIAN = Math.PI / 180;

const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  index,
}: PieLabelRenderProps): JSX.Element | null => {
  if (
    cx === undefined ||
    cy === undefined ||
    innerRadius === undefined ||
    outerRadius === undefined
  ) {
    return null;
  }

  // Ensure values are treated as numbers
  const radius =
    Number(innerRadius) + (Number(outerRadius) - Number(innerRadius)) * 0.5;
  const x = Number(cx) + radius * Math.cos(-Number(midAngle) * RADIAN);
  const y = Number(cy) + radius * Math.sin(-Number(midAngle) * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor={x > Number(cx) ? "start" : "end"}
      dominantBaseline="central"
    >
      {`${(Number(percent) * 100).toFixed(0)}%`}
    </text>
  );
};

const PieChartComponent = ({ data }: DataEntry) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [outerRadius, setOuterRadius] = useState(110);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const width = entry.contentRect.width;
        const height = entry.contentRect.height;

        // Set the outerRadius to 80% of the smaller dimension (width or height)
        setOuterRadius(Math.min(width, height) * 0.35); // 80% of Pie element means 40% of total container
      }
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
    // eslint-disable-next-line
  }, []);
  return (
    <div className="pieChartComponent" ref={containerRef}>
      <div className="chartHeader">
        <span className="header">Questions answered</span>
        <span className="subheader">
          Distribution of answers for this evaluation
        </span>
      </div>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart width={400} height={400}>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderCustomizedLabel}
            outerRadius={outerRadius}
            fill="#8884d8"
            dataKey="value"
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[index % COLORS.length]}
              />
            ))}
          </Pie>
          {/* Add Legend Component */}
          <Legend
            layout="horizontal"
            align="center"
            verticalAlign="bottom"
            iconType="circle"
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PieChartComponent;
